<template>
  <PModalBox
    :open-dialog="openDialog"
    header="Upload suppliers"
    size="large"
    height="large"
    @close-dialog="closeDialog"
  >
    <template #content>
      <v-row class="fill-height">
        <v-col class="pr-8" cols="12" md="5">
          <div v-if="authenticationStore.canUploadBusinessPartners()">
            <p class="mb-4">
              Upload a list of your suppliers and business partners. You must
              assign whether a company is a supplier or a business partner.
            </p>
            <p>
              See the definitions of “supplier” and “business partner” below
              according to the Transparency Act.
            </p>
          </div>
          <div v-else>
            <p class="mb-4">Upload a list of your suppliers.</p>
            <p>
              See the definition below of “supplier” according to the
              Transparency Act.
            </p>
          </div>
          <StandardCardContainer
            background-color="var(--tint-light-yellow-3)"
            radius="small"
            class="mt-4 definition-card"
          >
            <div>
              <h2>Definition of <em>supplier</em></h2>
              <p class="appkit-component">
                Any party in the chain of suppliers and sub-suppliers that
                supplies or produces goods, services or other input factors
                included in an enterprise's delivery of services or production
                of goods from the raw material stage to a finished product.
              </p>
              <ul class="ml-6 my-2 appkit-component">
                <li>
                  The supply chain encompasses all parties involved in the
                  process of moving and processing a product from a raw material
                  to a finished product
                </li>
                <li>
                  Input factors include, but are not limited to, raw materials,
                  components, services and transportation services.
                </li>
              </ul>
              <p class="appkit-component">
                Suppliers do not include typical business partners such as the
                cleaning services cleaning your office, the company that
                provides you with office chairs or your marketing agency.
              </p>
            </div>
            <div v-if="authenticationStore.canUploadBusinessPartners()">
              <h2 class="mt-4">Definition of <em>business partner</em></h2>
              <p class="appkit-component">
                By business partner it means anyone who supplies goods or
                services directly to the business, but who is not part of the
                supply chain.
              </p>
              <p class="appkit-component">
                Typical business partners include the cleaning company that
                cleans your office, your marketing agency and the company
                providing office chairs.
              </p>
            </div>
          </StandardCardContainer>
        </v-col>
        <v-col>
          <StandardCardContainer
            border-color="var(--light-grey-1)"
            class="fill-height"
          >
            <v-tabs v-model="tab">
              <v-tab value="upload">Upload a list</v-tab>
              <v-tab value="manual">Add manually</v-tab>
            </v-tabs>
            <v-window v-model="tab" class="supplier-window">
              <v-window-item value="upload">
                <v-container>
                  <v-row>
                    <v-col class="text-left">
                      <DownloadSupplierTemplate
                        :download-customer-template="
                          authenticationStore.canDownloadCustomerExcelTemplate()
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-8">
                    <v-col>
                      <UploadSupplierTemplate
                        @suppliers-uploaded="(val) => saveSuppliers(val)"
                        @file-deleted="resetUpload"
                      />
                      <v-alert
                        class="mt-2"
                        v-if="errorMessages.length > 0"
                        variant="outlined"
                        type="error"
                        title="Error"
                      >
                        <div
                          v-for="errorMessage in errorMessages"
                          class="appkit-tooltip"
                        >
                          {{ errorMessage }}
                        </div>
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-container>
              </v-window-item>
              <v-window-item value="manual">
                <InputSingleSupplier
                  class="my-4"
                  :can-upload-business-partner="
                    authenticationStore.canUploadBusinessPartners()
                  "
                  @change="onAddedSupplier"
                />
              </v-window-item>
            </v-window>
            <PModalBox
              :open-dialog="showOverwriteWarning"
              header="Warning"
              size="small"
              @close-dialog="showOverwriteWarning = false"
            >
              <template #content>
                Your existing suppliers will be overwritten. Do you want to
                continue?
              </template>
              <template #rightbutton>
                <PPrimaryButton
                  @click="
                    showOverwriteWarning = false;
                    shouldOverwrite = true;
                    uploadSuppliersForm();
                  "
                  >Continue</PPrimaryButton
                >
              </template>
              <template #leftbutton>
                <PSecondaryButton @click="showOverwriteWarning = false"
                  >Cancel</PSecondaryButton
                >
              </template>
            </PModalBox>
            <PModalBox
              :open-dialog="showFixErrorsModal"
              header="Correct errors"
              @close-dialog="showFixErrorsModal = false"
            >
              <template #content>
                There were errors in your uploaded file. Please review the error
                messages and correct the errors.
              </template>
              <template #rightbutton>
                <PPrimaryButton @click="showFixErrorsModal = false"
                  >Continue</PPrimaryButton
                >
              </template>
            </PModalBox>
          </StandardCardContainer>
        </v-col>
      </v-row>
    </template>
    <template #rightbutton>
      <PPrimaryButton @click="uploadSuppliersForm"> Upload </PPrimaryButton>
    </template>
    <template #leftbutton>
      <PSecondaryButton @click="closeDialog"> Cancel </PSecondaryButton>
    </template>
  </PModalBox>
</template>

<script setup lang="ts">
import PModalBox from "@/components/custom/PModalBox.vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import { computed, ref, type Ref, onBeforeMount } from "vue";
import DownloadSupplierTemplate from "@/views/upload-suppliers/DownloadSupplierTemplate.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import UploadSupplierTemplate from "@/views/upload-suppliers/UploadSupplierTemplate.vue";
import { useSuppliersStore } from "@/stores/suppliersStore";
import { useSenderStore } from "@/stores/sender";
import { useSnackBarStore } from "@/stores/snackBarStore";
import InputSingleSupplier from "@/views/supplier-registry/InputSingleSupplier.vue";
import { uploadSuppliers } from "@/services/suppliersService";

defineProps<{
  openDialog: boolean;
}>();

const emit = defineEmits<{
  (e: "closeDialog"): void;
  (e: "uploadedSuppliers"): void;
}>();

const tab = ref(null);
const authenticationStore = useAuthenticationStore();
const suppliersFormData = ref(new FormData());
const hasUploadedSuppliers = ref(false);
const errorMessages: Ref<string[]> = ref([]);
const showOverwriteWarning = ref(false);
const shouldOverwrite = ref(false);
const suppliers = computed(() => suppliersStore.suppliers);
const suppliersStore = useSuppliersStore();
const loading = ref(false);
const showFixErrorsModal = ref(false);
const senderStore = useSenderStore();
const snackBarStore = useSnackBarStore();

function closeDialog() {
  resetUpload();
  emit("closeDialog");
}

function saveSuppliers(formData: FormData) {
  suppliersFormData.value = formData;
  hasUploadedSuppliers.value = true;
}
function resetUpload() {
  hasUploadedSuppliers.value = false;
  errorMessages.value = [];
}

async function uploadSuppliersForm() {
  try {
    if (!hasUploadedSuppliers.value && suppliers.value.length > 0) {
      emit("uploadedSuppliers");
    } else if (hasUploadedSuppliers.value && errorMessages.value.length > 0) {
      showFixErrorsModal.value = true;
    } else if (
      hasUploadedSuppliers.value &&
      suppliers.value.length > 0 &&
      !shouldOverwrite.value
    ) {
      showOverwriteWarning.value = true;
    } else {
      loading.value = true;
      await uploadSuppliers(suppliersFormData.value);
      emit("uploadedSuppliers");
    }
  } catch (e) {
    //@ts-ignore
    errorMessages.value = Object.values(e.response.data).flat();
  } finally {
    loading.value = false;
  }
}

onBeforeMount(async () => {
  senderStore.canSendToBP = authenticationStore.canUploadBusinessPartners();
});

async function onAddedSupplier() {
  await getUpdatedSupplierList();
}

async function getUpdatedSupplierList() {
  try {
    await suppliersStore.setSupplierList();
  } catch (error: any) {
    snackBarStore.showError(error.message);
  }
}
</script>

<style scoped lang="scss">
.definition-card {
  h2 {
    font-size: 16px;
  }
}
</style>
